/**
 * Hook for fetching and updating home preferences
 * 
 * Uses React Query with semi-static caching strategy (30 minutes)
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { getHomePrefs, saveHomePrefs } from '../../api/homeApi';
import { homeKeys } from '../../lib/queryKeys';
import { queryOptions } from '../../lib/queryClient';

// Track retry attempts per company to prevent concurrent retries that could cause rate limiting
const retryAttempts = new Map<string, number>();

export interface HomePrefs {
  pinned: string[];
  hidden: string[];
  order: string[];
  version: number;
}

/**
 * Hook to fetch home preferences
 * 
 * @param company - Company slug (required)
 * @param options - Additional React Query options
 * @returns Query result with preferences data
 * 
 * @example
 * ```tsx
 * const { data: prefs, isLoading } = useHomePrefs(company);
 * ```
 */
export function useHomePrefs(
  company: string | undefined,
  options?: Omit<UseQueryOptions<HomePrefs>, 'queryKey' | 'queryFn'>
) {
  const queryClient = useQueryClient();
  
  // Check for cached data to use as initialData (like Clients page)
  const cachedData = queryClient.getQueryData<HomePrefs>(homeKeys.prefs(company || ''));
  
  return useQuery<HomePrefs>({
    queryKey: homeKeys.prefs(company || ''),
    queryFn: async () => {
      if (!company) {
        throw new Error('Company is required');
      }
      try {
        console.log('[useHomePrefs] queryFn called', { company });
        
        // 🔥 CRITICAL: Add timeout wrapper to ensure query never hangs forever
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('getHomePrefs timeout after 35 seconds'));
          }, 35000); // 35 seconds (slightly longer than API timeout of 30s)
        });
        
        const data = await Promise.race([
          getHomePrefs(company),
          timeoutPromise,
        ]);
        
        console.log('[useHomePrefs] queryFn resolved', { hasData: !!data });
        return data || { pinned: [], hidden: [], order: [], version: 1 };
      } catch (err: any) {
        console.error('[useHomePrefs] queryFn error', {
          error: err?.message,
          code: err?.code,
          status: err?.response?.status,
        });
        // 🔥 CRITICAL: Always throw to ensure React Query knows the query failed
        throw err;
      }
    },
    enabled: !!company,
    retry: 1, // Only retry once to avoid long waits
    initialData: cachedData, // Use cached data if available (from prefetch/bootstrap)
    placeholderData: cachedData || keepPreviousData, // Use cached data as placeholder for instant render
    // Cache-first: treat as static until explicitly invalidated (plan/badge changes)
    staleTime: Infinity, // Home prefs are static - only invalidate when plan/badge changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });
}

/**
 * Hook to save home preferences (mutation)
 * 
 * @param company - Company slug (required)
 * @returns Mutation function and status
 * 
 * @example
 * ```tsx
 * const { mutate: savePrefs, isPending } = useSaveHomePrefs(company);
 * savePrefs({ pinned: ['tab1'], hidden: [], order: [], version: 1 });
 * ```
 */
export function useSaveHomePrefs(company: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (prefs: HomePrefs) => {
      if (!company) {
        throw new Error('Company is required');
      }
      
      // Try to save with current prefs
      try {
        const result = await saveHomePrefs(company, prefs);
        // Clear retry count on success (in case it was set from a previous retry)
        retryAttempts.delete(company);
        return result;
      } catch (err: any) {
        // Handle rate limit (429) - don't retry automatically, let caller handle it
        // Retrying immediately would likely hit the rate limit again
        if (err?.response?.status === 429) {
          console.warn('[useSaveHomePrefs] Rate limit (429) - not retrying automatically to prevent further rate limiting');
          retryAttempts.delete(company);
          throw err;
        }
        
        // Handle version conflict (409) - refetch latest data and retry once with backoff
        if (err?.response?.status === 409 || err?.message?.includes('Version conflict')) {
          // Check if another retry is already in progress for this company
          // This prevents multiple concurrent retries that could exhaust rate limits
          const currentRetries = retryAttempts.get(company) || 0;
          if (currentRetries > 0) {
            console.warn('[useSaveHomePrefs] Retry already in progress for this company, skipping to prevent rate limiting');
            // Don't retry - just throw the error and let the caller handle it
            throw err;
          }
          
          // Mark that we're retrying for this company
          retryAttempts.set(company, 1);
          
          try {
            console.log('[useSaveHomePrefs] Version conflict detected, refetching latest prefs and retrying after delay...');
            
            // Add backoff delay before retrying (500ms) to prevent rapid-fire requests
            // This helps avoid hitting rate limits when multiple saves conflict simultaneously
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Refetch the latest preferences from server
            const latestPrefs = await getHomePrefs(company);
            
            // Merge the new prefs with the latest version from server
            // This ensures we keep the user's intended changes but with the correct version
            const mergedPrefs: HomePrefs = {
              pinned: prefs.pinned,
              hidden: prefs.hidden,
              order: prefs.order,
              version: latestPrefs.version, // Use the latest version from server
            };
            
            // Retry once with the merged prefs (latest version + user's changes)
            const result = await saveHomePrefs(company, mergedPrefs);
            // Clear retry count on success
            retryAttempts.delete(company);
            return result;
          } catch (retryErr: any) {
            // Clear retry count on failure
            retryAttempts.delete(company);
            
            // If retry also fails with 429, don't throw immediately - let the error propagate
            // This could happen if another save happened between our refetch and retry
            throw retryErr;
          }
        }
        
        // Clear retry count for other errors (in case it was set from a previous retry)
        retryAttempts.delete(company);
        
        // Re-throw other errors
        throw err;
      }
    },
    onSuccess: (data) => {
      // Optimistically update cache
      if (company) {
        queryClient.setQueryData(homeKeys.prefs(company), data);
      }
    },
  });
}


